import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { STAFF_SHOPIFY_ORDERS } from '@staff-app/graphql-client';
import type {
  ShopifyFinancialFilter,
  ShopifyFulfillmentFilter,
  ShopifyOrderPage,
} from '@staff-app/shared-types';
import { useDebounced } from '@/features/order-devices/hooks/useOrderDeviceTasks';

export const SHOPIFY_ORDER_PAGE_SIZE = 25;

export interface ShopifyOrderFilters {
  search: string;
  fulfillment: ShopifyFulfillmentFilter;
  financial: ShopifyFinancialFilter;
  createdFrom: string;
  createdTo: string;
}

export const EMPTY_FILTERS: ShopifyOrderFilters = {
  search: '',
  fulfillment: 'any',
  financial: 'any',
  createdFrom: '',
  createdTo: '',
};

/**
 * Cursor paging over Shopify orders.
 *
 * Shopify connections have no offset, so a page NUMBER cannot be reconstructed — you can only move
 * forward from a cursor. To still offer "Previous", we keep the cursor that opened each page in a
 * stack: going back pops one rather than trying to compute it. This is why there is no "jump to
 * page 7", and why the filters reset the stack rather than preserving position.
 */
export function useShopifyOrders() {
  const [filters, setFilters] = useState<ShopifyOrderFilters>(EMPTY_FILTERS);
  // Cursor for the page currently shown; null = first page. The stack holds the pages behind it.
  const [cursor, setCursor] = useState<string | null>(null);
  const [stack, setStack] = useState<(string | null)[]>([]);

  const debouncedSearch = useDebounced(filters.search, 350);

  // A changed filter invalidates every cursor we hold — they are positions in the OLD result set,
  // and reusing one would page through a list that no longer exists.
  useEffect(() => {
    setCursor(null);
    setStack([]);
  }, [debouncedSearch, filters.fulfillment, filters.financial, filters.createdFrom, filters.createdTo]);

  const input = useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
      fulfillment: filters.fulfillment === 'any' ? undefined : filters.fulfillment,
      financial: filters.financial === 'any' ? undefined : filters.financial,
      createdFrom: filters.createdFrom || undefined,
      createdTo: filters.createdTo || undefined,
      first: SHOPIFY_ORDER_PAGE_SIZE,
      after: cursor || undefined,
    }),
    [debouncedSearch, filters.fulfillment, filters.financial, filters.createdFrom, filters.createdTo, cursor],
  );

  const { data, loading, error, refetch } = useQuery<{ staffShopifyOrders: ShopifyOrderPage }>(
    STAFF_SHOPIFY_ORDERS,
    { variables: { input }, fetchPolicy: 'cache-and-network' },
  );

  const page = data?.staffShopifyOrders;

  const next = useCallback(() => {
    if (!page?.hasNextPage || !page.endCursor) return;
    setStack((s) => [...s, cursor]);
    setCursor(page.endCursor);
  }, [page, cursor]);

  const prev = useCallback(() => {
    setStack((s) => {
      if (!s.length) return s;
      setCursor(s[s.length - 1]);
      return s.slice(0, -1);
    });
  }, []);

  return {
    filters,
    setFilters,
    rows: page?.nodes ?? [],
    hasNextPage: Boolean(page?.hasNextPage),
    hasPrevPage: stack.length > 0,
    // True only for the unfiltered first page — the caller uses it to say "most recent orders"
    // rather than implying the table is everything.
    isDefaultView:
      !debouncedSearch.trim() &&
      filters.fulfillment === 'any' &&
      filters.financial === 'any' &&
      !filters.createdFrom &&
      !filters.createdTo,
    pageIndex: stack.length,
    loading,
    error,
    next,
    prev,
    refetch,
  };
}
